import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Check, ArrowRightLeft, X, Clock, MoreHorizontal, Globe, Phone, Stethoscope } from 'lucide-react';
import { useNotifications, type FeedAction, type FeedItem } from '../notifications/NotificationsContext';

// List chips keep their per-action colours; only late arrivals are red (the
// colour inversion requested applies to the bell COUNT badge, not these rows).
const ACTION: Record<FeedAction, { icon: typeof Check; cls: string }> = {
  BOOKED: { icon: Check, cls: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300' },
  RESCHEDULED: { icon: ArrowRightLeft, cls: 'bg-amber-500/12 text-amber-700 dark:text-amber-300' },
  CANCELLED: { icon: X, cls: 'bg-rose-500/12 text-rose-700 dark:text-rose-300' },
  LATE: { icon: Clock, cls: 'bg-red-500/12 text-red-700 dark:text-red-300' },
  OTHER: { icon: MoreHorizontal, cls: 'bg-slate-500/12 text-slate-700 dark:text-slate-300' },
};
const PLATFORM_ICON = { WEB: Globe, PHONE: Phone, SUPERDOC: Stethoscope } as const;

/** Header notification bell: unseen count (per-user), a dropdown of the latest
 *  activity, and "see all" → the Журнал. Opening the bell marks seen (clears the
 *  count for this user only). Hidden for roles without the feed. */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { allowed, items, unseenCount, unseenLateCount, isUnseen, markSeen } = useNotifications();
  const [open, setOpen] = useState(false);
  // Which items were new AT OPEN time — kept highlighted for the whole open
  // session so you can see what's new, even though opening clears the badge.
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);

  const rtf = useMemo(
    () => new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }),
    [i18n.language],
  );
  const ago = (iso: string) => {
    const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return rtf.format(-s, 'second');
    const m = Math.round(s / 60);
    if (m < 60) return rtf.format(-m, 'minute');
    const h = Math.round(m / 60);
    if (h < 24) return rtf.format(-h, 'hour');
    return rtf.format(-Math.round(h / 24), 'day');
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  if (!allowed) return null;

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      // Snapshot the currently-new ids (before markSeen flips them), keep them
      // highlighted while open; opening still clears this user's unseen count.
      setNewIds(new Set(items.filter(isUnseen).map((n) => n.id)));
      markSeen();
    } else {
      setNewIds(new Set());
    }
  }

  const recent = items.slice(0, 8);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        aria-label={t('notifications.bellTitle')}
        onClick={toggle}
        className="relative inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <Bell className="h-5 w-5" />
        {unseenCount > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 grid min-w-[16px] place-items-center rounded-full border-2 border-card bg-blue-500 px-1 text-[9px] font-extrabold leading-none text-white tabular-nums">
            {unseenCount > 99 ? '99+' : unseenCount}
          </span>
        ) : null}
        {/* Late arrivals get their own red badge (top-left), kept separate
            from the routine blue count so "a patient is running late" stands out. */}
        {unseenLateCount > 0 ? (
          <span
            aria-label={t('notifications.lateBadge')}
            className="absolute -top-0.5 -left-0.5 grid min-w-[16px] place-items-center rounded-full border-2 border-card bg-red-500 px-1 text-[9px] font-extrabold leading-none text-white tabular-nums"
          >
            {unseenLateCount > 99 ? '99+' : unseenLateCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[min(360px,calc(100vw-32px))] rounded-xl border border-border bg-card p-2 shadow-lg">
          <div className="flex items-center justify-between px-2 py-1.5">
            <span className="text-[13px] font-semibold">{t('notifications.bellTitle')}</span>
          </div>

          <div className="flex max-h-[320px] flex-col gap-0.5 overflow-y-auto">
            {recent.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                {t('notifications.empty')}
              </div>
            ) : (
              recent.map((n) => <Row key={n.id} n={n} unseen={newIds.has(n.id)} ago={ago(n.createdAt)} onOpen={() => { setOpen(false); navigate(`/activity?highlight=${encodeURIComponent(n.id)}`); }} />)
            )}
          </div>

          <div className="mt-1 border-t border-border pt-1 text-center">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/activity'); }}
              className="p-1.5 text-[12.5px] font-semibold text-accent hover:underline"
            >
              {t('notifications.viewAll')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Row({ n, unseen, ago, onOpen }: { n: FeedItem; unseen: boolean; ago: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const a = ACTION[n.action] ?? ACTION.OTHER;
  const Icon = a.icon;
  const PIcon = PLATFORM_ICON[n.platform] ?? Globe;
  const subject = n.domain === 'rehab' ? n.patientName ?? '' : [n.doctorName, n.patientName].filter(Boolean).join(' · ');
  const when = n.action === 'RESCHEDULED' && n.oldWhenText ? `${n.oldWhenText} → ${n.whenText ?? ''}` : n.whenText ?? '';

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-muted ${
        unseen ? 'bg-accent/10 ring-1 ring-inset ring-accent/30' : ''
      }`}
    >
      <span className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg ${a.cls}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className={`truncate text-[13px] ${unseen ? 'font-semibold' : 'font-medium'}`}>
            {t(`enums.${n.action}`, n.action)}
          </span>
          <PIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
          {unseen ? (
            <span className="ml-auto shrink-0 rounded-full bg-accent px-1.5 text-[9px] font-bold uppercase leading-[15px] tracking-wide text-accent-foreground">
              {t('notifications.new')}
            </span>
          ) : null}
        </span>
        {subject ? <span className="block truncate text-[11.5px] text-muted-foreground">{subject}</span> : null}
        <span className="block text-[11px] text-muted-foreground/80">
          {[when, ago].filter(Boolean).join(' · ')}
        </span>
      </span>
    </button>
  );
}
